import { useLayoutEffect, useRef, useState } from 'react'

/**
 * One-tap segmented choice with a sliding pill (Charles, 2026-08-06): the
 * navy fill GLIDES from the old choice to the new one instead of teleporting.
 * Measured with offsets so it works across wrapped rows; buttons keep their
 * own borders so the un-animated look is pixel-identical. Shared by the
 * session form and the client form. Tapping the active option clears it.
 */
export default function Segmented({ options, value, onChange, disabled = [] }) {
  const off = new Set(disabled)
  const wrapRef = useRef(null)
  const btnRefs = useRef(new Map())
  const [pill, setPill] = useState(null)

  useLayoutEffect(() => {
    const el = value != null ? btnRefs.current.get(value) : null
    if (!el) {
      setPill(null)
      return
    }
    const place = () =>
      setPill({ left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight })
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [value, options])

  return (
    <div ref={wrapRef} className="relative flex flex-wrap gap-2">
      {pill && (
        <span
          aria-hidden
          className="absolute bg-navy transition-all duration-200 ease-out"
          style={{ left: pill.left, top: pill.top, width: pill.width, height: pill.height }}
        />
      )}
      {options.map((opt) => {
        const on = value === opt
        const dis = off.has(opt) && !on
        return (
          <button
            key={opt}
            ref={(el) => (el ? btnRefs.current.set(opt, el) : btnRefs.current.delete(opt))}
            type="button"
            disabled={dis}
            aria-disabled={dis}
            onClick={() => onChange(on ? null : opt)}
            className={
              'tap relative z-10 border px-3 py-1.5 text-sm font-medium transition-colors duration-200 ' +
              (on ? 'border-navy text-white' : dis ? 'cursor-not-allowed border-dashed border-faint text-faint line-through' : 'border-hairline text-ink active:bg-grouped')
            }
            style={{ borderWidth: '1.5px' }}
          >
            {opt}
          </button>
        )
      })}
    </div>
  )
}
